import React from 'react';
import { useShortcutContext } from './ShortcutProvider';

export type UseShortcutOptions = {
    /** Registered but inert while `false`; a press calls `onUnavailable` instead of `handler`. */
    enabled?: boolean;
    /** Shown instead of doing nothing when a disabled binding is pressed. */
    onUnavailable?: () => void;
};

const useLatestRef = <T>(value: T): React.RefObject<T> => {
    const ref = React.useRef(value);
    ref.current = value;
    return ref;
};

/**
 * The binding hook features use to attach a handler to a registry id. Looked
 * up against the provider's `registry` for its combination and scope; the
 * caller supplies only behaviour.
 */
export const useShortcut = (
    id: string,
    handler: (event: KeyboardEvent) => void,
    options: UseShortcutOptions = {},
): void => {
    const { registerBinding, unregisterBinding } = useShortcutContext();
    const enabled = options.enabled ?? true;
    const hasOnUnavailable = options.onUnavailable !== undefined;
    const handlerRef = useLatestRef(handler);
    const onUnavailableRef = useLatestRef(options.onUnavailable);

    React.useEffect(() => {
        registerBinding(id, {
            handle: (event) => handlerRef.current(event),
            enabled,
            onUnavailable: hasOnUnavailable
                ? () => onUnavailableRef.current?.()
                : undefined,
        });
        return () => unregisterBinding(id);
    }, [
        id,
        enabled,
        hasOnUnavailable,
        registerBinding,
        unregisterBinding,
        handlerRef,
        onUnavailableRef,
    ]);
};
